import { useCallback, useEffect, useState } from 'react'
import { ethers } from 'ethers'
import { TransactionResponse } from '@ethersproject/providers'
import { useActiveWeb3React } from './index'
import { useOrderBookContract, useTokenContract, useRouterContract } from './useContract'
import { useTransactionAdder } from '../state/transactions/hooks'
import {
  ORDERBOOK_ADDRESS,
  ORDERBOOK_API_BASE,
  USDT_TOKEN_ADDRESS,
  WMUD_ADDRESS,
  OrderStatus,
  USDT_DECIMALS,
  MUD_DECIMALS,
  DEFAULT_ADMIN_ROLE
} from '../constants/orderbook'
import { calculateGasMargin } from '../utils'
import { MaxUint256 } from '@ethersproject/constants'

// ============ Types ============

export interface OrderInfo {
  orderId: number
  user: string
  type: number
  price: string
  amount: string
  filled: string
  remaining: string
  feeRate: number
  status: number
  isPublic: boolean
  totalReceived: string
  totalFee: string
  createdAt: string
  updatedAt: string
  txHash?: string
  fills?: FillInfo[]
}

export interface FillInfo {
  fillId: number
  orderId: number
  usdtAmount: string
  mudAmount: string
  fee: string
  txHash: string
  blockNumber: number
  timestamp: string
}

// ============ Format Helpers ============

export function formatUsdtAmount(amount: string): string {
  const num = parseFloat(amount) / Math.pow(10, USDT_DECIMALS)
  if (num === 0) return '0'
  if (num < 0.01) return '<0.01'
  return num.toFixed(2)
}

export function formatMudAmount(amount: string): string {
  const num = parseFloat(amount) / Math.pow(10, MUD_DECIMALS)
  if (num === 0) return '0'
  if (num < 0.0001) return '<0.0001'
  return num.toFixed(4)
}

export function formatPrice(price: string): string {
  const num = parseFloat(price) / Math.pow(10, USDT_DECIMALS)
  if (num === 0) return '0'
  return num.toFixed(4)
}

export function getOrderStatusText(status: number): string {
  switch (status) {
    case OrderStatus.OPEN:
      return 'orderStatusOpen'
    case OrderStatus.FILLED:
      return 'orderStatusFilled'
    case OrderStatus.CANCELLED:
      return 'orderStatusCancelled'
    default:
      return 'unknown'
  }
}

// ============ Current Price Hook ============

export function useCurrentPrice(): { price: string; loading: boolean; lastUpdated: number; refresh: () => void } {
  const [price, setPrice] = useState('0')
  const [loading, setLoading] = useState(true)
  const [lastUpdated, setLastUpdated] = useState(0)
  const routerContract = useRouterContract(false)

  const fetchPrice = useCallback(async (isInitial = false) => {
    if (!routerContract) return
    try {
      if (isInitial) setLoading(true)
      const amounts = await routerContract.getAmountsOut(ethers.utils.parseEther('1'), [WMUD_ADDRESS, USDT_TOKEN_ADDRESS])
      setPrice(amounts[1].toString())
    } catch (e) {
      console.error('Failed to get price:', e)
      try {
        const res = await fetch(`${ORDERBOOK_API_BASE}/price`)
        const data = await res.json()
        if (data.code === 0) {
          setPrice(data.data.price)
        }
      } catch (err) {
        console.error('Failed to get price from API:', err)
      }
    } finally {
      setLoading(false)
      setLastUpdated(Date.now())
    }
  }, [routerContract])

  useEffect(() => {
    fetchPrice(true)
    const interval = setInterval(() => fetchPrice(false), 5000)
    return () => clearInterval(interval)
  }, [fetchPrice])

  return { price, loading, lastUpdated, refresh: () => fetchPrice(true) }
}

// ============ USDT Allowance Hook ============

export function useUsdtAllowance(): {
  allowance: ethers.BigNumber
  loading: boolean
  refresh: () => void
} {
  const { account } = useActiveWeb3React()
  const usdtContract = useTokenContract(USDT_TOKEN_ADDRESS, false)
  const [allowance, setAllowance] = useState(ethers.BigNumber.from(0))
  const [loading, setLoading] = useState(true)

  const fetchAllowance = useCallback(async () => {
    if (!usdtContract || !account) {
      setAllowance(ethers.BigNumber.from(0))
      setLoading(false)
      return
    }
    try {
      const result = await usdtContract.allowance(account, ORDERBOOK_ADDRESS)
      setAllowance(ethers.BigNumber.from(result.toString()))
    } catch (e) {
      console.error('Failed to get allowance:', e)
    } finally {
      setLoading(false)
    }
  }, [usdtContract, account])

  useEffect(() => {
    fetchAllowance()
  }, [fetchAllowance])

  return { allowance, loading, refresh: fetchAllowance }
}

// ============ Approve USDT Hook ============

export function useApproveUSDT(): {
  approve: () => Promise<TransactionResponse | undefined>
  pending: boolean
} {
  const usdtContract = useTokenContract(USDT_TOKEN_ADDRESS, true)
  const addTransaction = useTransactionAdder()
  const [pending, setPending] = useState(false)

  const approve = useCallback(async () => {
    if (!usdtContract) return undefined
    try {
      setPending(true)
      const estimatedGas = await usdtContract.estimateGas.approve(ORDERBOOK_ADDRESS, MaxUint256).catch(() => {
        return usdtContract.estimateGas.approve(ORDERBOOK_ADDRESS, ethers.constants.MaxUint256)
      })

      const response: TransactionResponse = await usdtContract.approve(ORDERBOOK_ADDRESS, MaxUint256, {
        gasLimit: calculateGasMargin(estimatedGas)
      })

      addTransaction(response, {
        summary: 'Approve USDT',
        approval: { tokenAddress: USDT_TOKEN_ADDRESS, spender: ORDERBOOK_ADDRESS }
      })

      return response
    } catch (e) {
      console.error('Failed to approve USDT:', e)
      throw e
    } finally {
      setPending(false)
    }
  }, [usdtContract, addTransaction])

  return { approve, pending }
}

// ============ Create Buy Order Hook ============

export function useCreateBuyOrder(): {
  createBuyOrder: (
    price: string,
    usdtAmount: string,
    feeRate: number,
    isPublic: boolean
  ) => Promise<TransactionResponse | undefined>
  pending: boolean
} {
  const orderBookContract = useOrderBookContract()
  const addTransaction = useTransactionAdder()
  const [pending, setPending] = useState(false)

  const createBuyOrder = useCallback(
    async (price: string, usdtAmount: string, feeRate: number, isPublic: boolean) => {
      if (!orderBookContract) return undefined
      try {
        setPending(true)

        const priceBN = ethers.utils.parseUnits(price, USDT_DECIMALS)
        const amountBN = ethers.utils.parseUnits(usdtAmount, USDT_DECIMALS)

        const estimatedGas = await orderBookContract.estimateGas.createBuyOrder(priceBN, amountBN, feeRate, isPublic)

        const response: TransactionResponse = await orderBookContract.createBuyOrder(
          priceBN,
          amountBN,
          feeRate,
          isPublic,
          { gasLimit: calculateGasMargin(estimatedGas) }
        )

        addTransaction(response, {
          summary: `Create buy order: ${usdtAmount} USDT @ ${price}`
        })

        return response
      } catch (e) {
        console.error('Failed to create buy order:', e)
        throw e
      } finally {
        setPending(false)
      }
    },
    [orderBookContract, addTransaction]
  )

  return { createBuyOrder, pending }
}

// ============ Create Sell Order Hook ============

export function useCreateSellOrder(): {
  createSellOrder: (
    price: string,
    mudAmount: string,
    feeRate: number,
    isPublic: boolean
  ) => Promise<TransactionResponse | undefined>
  pending: boolean
} {
  const orderBookContract = useOrderBookContract()
  const addTransaction = useTransactionAdder()
  const [pending, setPending] = useState(false)

  const createSellOrder = useCallback(
    async (price: string, mudAmount: string, feeRate: number, isPublic: boolean) => {
      if (!orderBookContract) return undefined
      try {
        setPending(true)

        const priceBN = ethers.utils.parseUnits(price, USDT_DECIMALS)
        const valueBN = ethers.utils.parseEther(mudAmount)

        const estimatedGas = await orderBookContract.estimateGas.createSellOrder(priceBN, feeRate, isPublic, {
          value: valueBN
        })

        const response: TransactionResponse = await orderBookContract.createSellOrder(priceBN, feeRate, isPublic, {
          value: valueBN,
          gasLimit: calculateGasMargin(estimatedGas)
        })

        addTransaction(response, {
          summary: `Create sell order: ${mudAmount} MUD @ ${price}`
        })

        return response
      } catch (e) {
        console.error('Failed to create sell order:', e)
        throw e
      } finally {
        setPending(false)
      }
    },
    [orderBookContract, addTransaction]
  )

  return { createSellOrder, pending }
}

// ============ Cancel Order Hook ============

export function useCancelOrder(): {
  cancelOrder: (orderId: number) => Promise<TransactionResponse | undefined>
  pending: boolean
} {
  const orderBookContract = useOrderBookContract()
  const addTransaction = useTransactionAdder()
  const [pending, setPending] = useState(false)

  const cancelOrder = useCallback(
    async (orderId: number) => {
      if (!orderBookContract) return undefined
      try {
        setPending(true)

        const estimatedGas = await orderBookContract.estimateGas.cancelOrder(orderId)

        const response: TransactionResponse = await orderBookContract.cancelOrder(orderId, {
          gasLimit: calculateGasMargin(estimatedGas)
        })

        addTransaction(response, {
          summary: `Cancel order #${orderId}`
        })

        return response
      } catch (e) {
        console.error('Failed to cancel order:', e)
        throw e
      } finally {
        setPending(false)
      }
    },
    [orderBookContract, addTransaction]
  )

  return { cancelOrder, pending }
}

// ============ Update Order Price Hook ============

export function useUpdateOrderPrice(): {
  updatePrice: (orderId: number, newPrice: string) => Promise<TransactionResponse | undefined>
  pending: boolean
} {
  const orderBookContract = useOrderBookContract()
  const addTransaction = useTransactionAdder()
  const [pending, setPending] = useState(false)

  const updatePrice = useCallback(
    async (orderId: number, newPrice: string) => {
      if (!orderBookContract) return undefined
      try {
        setPending(true)

        const priceBN = ethers.utils.parseUnits(newPrice, USDT_DECIMALS)
        const estimatedGas = await orderBookContract.estimateGas.updateOrderPrice(orderId, priceBN)

        const response: TransactionResponse = await orderBookContract.updateOrderPrice(orderId, priceBN, {
          gasLimit: calculateGasMargin(estimatedGas)
        })

        addTransaction(response, {
          summary: `Update order #${orderId} price to ${newPrice}`
        })

        return response
      } catch (e) {
        console.error('Failed to update order price:', e)
        throw e
      } finally {
        setPending(false)
      }
    },
    [orderBookContract, addTransaction]
  )

  return { updatePrice, pending }
}

// ============ User Orders Hook (API) ============

export function useUserOrders(page = 1, pageSize = 20, status?: number, type?: number): {
  orders: OrderInfo[]
  total: number
  loading: boolean
  refresh: () => void
} {
  const { account } = useActiveWeb3React()
  const [orders, setOrders] = useState<OrderInfo[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  const fetchOrders = useCallback(async () => {
    if (!account) {
      setOrders([])
      setTotal(0)
      return
    }
    try {
      setLoading(true)
      const statusParam = status !== undefined ? `&status=${status}` : ''
      const typeParam = type !== undefined ? `&type=${type}` : ''
      const res = await fetch(
        `${ORDERBOOK_API_BASE}/user-orders?address=${account}&page=${page}&pageSize=${pageSize}${statusParam}${typeParam}`
      )
      const data = await res.json()
      if (data.code === 0) {
        setOrders(data.data.items)
        setTotal(data.data.total)
      }
    } catch (e) {
      console.error('Failed to fetch user orders:', e)
    } finally {
      setLoading(false)
    }
  }, [account, page, pageSize, status, type])

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  return { orders, total, loading, refresh: fetchOrders }
}

// ============ All Orders (API GET /orders，管理员用) ============

function mapOrderItemToOrderInfo(item: any): OrderInfo {
  return {
    orderId: Number(item.orderId),
    user: String(item.user ?? ''),
    type: Number(item.type ?? 0),
    price: String(item.price ?? '0'),
    amount: String(item.amount ?? '0'),
    filled: String(item.filled ?? '0'),
    remaining: String(item.remaining ?? '0'),
    feeRate: Number(item.feeRate ?? 0),
    status: Number(item.status ?? 0),
    isPublic: Boolean(item.isPublic),
    totalReceived: String(item.totalReceived ?? '0'),
    totalFee: String(item.totalFee ?? '0'),
    createdAt: item.createdAt ? new Date(item.createdAt).toISOString() : '',
    updatedAt: item.updatedAt ? new Date(item.updatedAt).toISOString() : ''
  }
}

export function useAllOrdersFromApi(
  page = 1,
  pageSize = 20,
  status?: number,
  user?: string,
  type?: number
): { orders: OrderInfo[]; total: number; loading: boolean; refresh: () => void } {
  const [orders, setOrders] = useState<OrderInfo[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true)
      const statusParam = status !== undefined ? `&status=${status}` : ''
      const userParam = user?.trim() ? `&user=${encodeURIComponent(user.trim())}` : ''
      const typeParam = type !== undefined ? `&type=${type}` : ''
      const res = await fetch(
        `${ORDERBOOK_API_BASE}/orders?page=${page}&pageSize=${pageSize}${statusParam}${userParam}${typeParam}`
      )
      const data = await res.json()
      if (data.code === 0 && Array.isArray(data.data?.items)) {
        setOrders(data.data.items.map(mapOrderItemToOrderInfo))
        setTotal(data.data.total ?? 0)
      } else {
        setOrders([])
        setTotal(0)
      }
    } catch (e) {
      console.error('Failed to fetch all orders:', e)
      setOrders([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, status, user, type])

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  return { orders, total, loading, refresh: fetchOrders }
}

// ============ Order Book Depth (API GET /orderbook) ============

export interface OrderBookLevel {
  price: string
  amount: string
  total: string
}

export interface OrderBookDepthData {
  asks: OrderBookLevel[]
  bids: OrderBookLevel[]
  currentPrice: string
  buyVolume: string
  sellVolume: string
  buyPercent: number
  sellPercent: number
}

export function useOrderBookDepth(includePrivate = false): {
  data: OrderBookDepthData | null
  loading: boolean
  lastUpdated: number
  refresh: () => void
} {
  const [data, setData] = useState<OrderBookDepthData | null>(null)
  const [loading, setLoading] = useState(true)
  const [lastUpdated, setLastUpdated] = useState(0)

  const fetchDepth = useCallback(async () => {
    try {
      setLoading(true)
      const q = includePrivate ? '?includePrivate=true' : ''
      const res = await fetch(`${ORDERBOOK_API_BASE}/orderbook${q}`)
      const json = await res.json()
      if (json.code === 0 && json.data) {
        setData({
          asks: json.data.asks ?? [],
          bids: json.data.bids ?? [],
          currentPrice: json.data.currentPrice ?? '0',
          buyVolume: json.data.buyVolume ?? '0',
          sellVolume: json.data.sellVolume ?? '0',
          buyPercent: json.data.buyPercent ?? 0,
          sellPercent: json.data.sellPercent ?? 0
        })
      }
    } catch (e) {
      console.error('Failed to fetch orderbook depth:', e)
    } finally {
      setLoading(false)
      setLastUpdated(Date.now())
    }
  }, [includePrivate])

  useEffect(() => {
    fetchDepth()
    const interval = setInterval(fetchDepth, 10000)
    return () => clearInterval(interval)
  }, [fetchDepth])

  return { data, loading, lastUpdated, refresh: fetchDepth }
}

// ============ All Active Orders (from contract, for 全部挂单 tab) ============

function contractOrderToOrderInfo(order: any): OrderInfo {
  const o = Array.isArray(order) ? order : order
  const orderId = o.orderId ?? o[0]
  const user = o.user ?? o[1]
  const orderType = o.orderType ?? o[2]
  const price = o.price ?? o[3]
  const amount = o.amount ?? o[4]
  const filled = o.filled ?? o[5]
  const feeRate = o.feeRate ?? o[6]
  const status = o.status ?? o[7]
  const isPublic = o.isPublic ?? o[8]
  const totalReceived = o.totalReceived ?? o[9]
  const totalFee = o.totalFee ?? o[10]
  const createdAt = o.createdAt ?? o[11]
  const updatedAt = o.updatedAt ?? o[12]
  const amt = amount?.toString() ?? '0'
  const fl = filled?.toString() ?? '0'
  return {
    orderId: Number(orderId?.toString() ?? 0),
    user: user?.toString() ?? '',
    type: Number(orderType?.toString() ?? 0),
    price: price?.toString() ?? '0',
    amount: amt,
    filled: fl,
    remaining: (BigInt(amt) - BigInt(fl)).toString(),
    feeRate: Number(feeRate?.toString() ?? 0),
    status: Number(status?.toString() ?? 0),
    isPublic: isPublic ?? true,
    totalReceived: totalReceived?.toString() ?? '0',
    totalFee: totalFee?.toString() ?? '0',
    createdAt: createdAt ? new Date(Number(createdAt.toString()) * 1000).toISOString() : '',
    updatedAt: updatedAt ? new Date(Number(updatedAt.toString()) * 1000).toISOString() : ''
  }
}

export function useAllActiveOrders(page: number, pageSize: number): {
  orders: OrderInfo[]
  total: number
  loading: boolean
  refresh: () => void
} {
  const orderBookContract = useOrderBookContract(false)
  const [orders, setOrders] = useState<OrderInfo[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  const fetchOrders = useCallback(async () => {
    if (!orderBookContract) {
      setOrders([])
      setTotal(0)
      return
    }
    setLoading(true)
    try {
      const [count, ids] = await Promise.all([
        orderBookContract.getActiveOrdersCount(),
        orderBookContract.getActiveOrderIdsPaginated((page - 1) * pageSize, pageSize)
      ])
      const totalNum = Number(count.toString())
      const idArray = Array.isArray(ids) ? ids : []
      const orderPromises = idArray.map((id: any) => orderBookContract.getOrder(id.toString()))
      const rawOrders = await Promise.all(orderPromises)
      setOrders(rawOrders.map((o: any) => contractOrderToOrderInfo(o)))
      setTotal(totalNum)
    } catch (e) {
      console.error('Failed to fetch all active orders', e)
      setOrders([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [orderBookContract, page, pageSize])

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  return { orders, total, loading, refresh: fetchOrders }
}

// ============ Whitelist Check Hook ============

export function useWhitelistStatus(): boolean {
  const { account } = useActiveWeb3React()
  const [isWhitelisted, setIsWhitelisted] = useState(false)
  const orderBookContract = useOrderBookContract(false)

  useEffect(() => {
    if (!orderBookContract || !account) return
    orderBookContract
      .whitelist(account)
      .then((result: boolean) => setIsWhitelisted(result))
      .catch(() => setIsWhitelisted(false))
  }, [orderBookContract, account])

  return isWhitelisted
}

// ============ Contract Config Hook ============

export function useOrderBookConfig(): {
  minFeeRate: number
  minPrivateFeeRate: number
  minOrderValueUSDT: number
  maxOrdersPerUser: number
  loading: boolean
} {
  const orderBookContract = useOrderBookContract(false)
  const [config, setConfig] = useState({
    minFeeRate: 1,
    minPrivateFeeRate: 10,
    minOrderValueUSDT: 10,
    maxOrdersPerUser: 100,
    loading: true
  })

  useEffect(() => {
    if (!orderBookContract) return

    Promise.all([
      orderBookContract.minFeeRate(),
      orderBookContract.minPrivateFeeRate(),
      orderBookContract.minOrderValueUSDT(),
      orderBookContract.maxOrdersPerUser()
    ])
      .then(([minFee, minPrivateFee, minValue, maxOrders]) => {
        setConfig({
          minFeeRate: Number(minFee.toString()),
          minPrivateFeeRate: Number(minPrivateFee.toString()),
          minOrderValueUSDT: Number(minValue.toString()) / Math.pow(10, USDT_DECIMALS),
          maxOrdersPerUser: Number(maxOrders.toString()),
          loading: false
        })
      })
      .catch(() => {
        setConfig(prev => ({ ...prev, loading: false }))
      })
  }, [orderBookContract])

  return config
}

// ============ USDT Balance Hook ============

export function useUsdtBalance(): { balance: ethers.BigNumber; formatted: string; loading: boolean; refresh: () => void } {
  const { account } = useActiveWeb3React()
  const usdtContract = useTokenContract(USDT_TOKEN_ADDRESS, false)
  const [balance, setBalance] = useState(ethers.BigNumber.from(0))
  const [loading, setLoading] = useState(true)

  const fetchBalance = useCallback(() => {
    if (!usdtContract || !account) {
      setBalance(ethers.BigNumber.from(0))
      setLoading(false)
      return
    }
    setLoading(true)
    usdtContract
      .balanceOf(account)
      .then((bal: ethers.BigNumber) => {
        setBalance(bal)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [usdtContract, account])

  useEffect(() => {
    fetchBalance()
  }, [fetchBalance])

  const formatted = formatUsdtAmount(balance.toString())

  return { balance, formatted, loading, refresh: fetchBalance }
}

// ============ Role Check (EXECUTOR / DEFAULT_ADMIN) ============

const EXECUTOR_ROLE_BYTES = ethers.utils.id('EXECUTOR_ROLE')

export function useHasOrderBookRole(): { isAdmin: boolean; isExecutor: boolean; loading: boolean } {
  const { account } = useActiveWeb3React()
  const orderBookContract = useOrderBookContract(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [isExecutor, setIsExecutor] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!account) {
      setIsAdmin(false)
      setIsExecutor(false)
      setLoading(false)
      return
    }
    if (!orderBookContract) {
      setIsAdmin(false)
      setIsExecutor(false)
      setLoading(false)
      return
    }
    Promise.all([
      orderBookContract.hasRole(DEFAULT_ADMIN_ROLE, account),
      orderBookContract.hasRole(EXECUTOR_ROLE_BYTES, account)
    ])
      .then(([admin, executor]) => {
        setIsAdmin(admin)
        setIsExecutor(executor)
      })
      .catch(() => {
        setIsAdmin(false)
        setIsExecutor(false)
      })
      .finally(() => setLoading(false))
  }, [orderBookContract, account])

  return { isAdmin, isExecutor, loading }
}

// ============ Admin State (for 管理合约 tab) ============

export function useOrderBookAdminState(): {
  paused: boolean
  maxPriceMultiplier: number
  feeReceiver: string
  accumulatedUsdtFee: string
  accumulatedMudFee: string
  statistics: {
    orderCountOpen: number
    orderCountFilled: number
    orderCountCancelled: number
    openBuyUsdtTotal: string
    openSellMudTotal: string
    uniqueUserCount: number
    totalUsdtFeeCollected: string
    totalMudFeeCollected: string
  }
  loading: boolean
  refresh: () => void
} {
  const orderBookContract = useOrderBookContract(false)
  const [state, setState] = useState({
    paused: false,
    maxPriceMultiplier: 5,
    feeReceiver: '',
    accumulatedUsdtFee: '0',
    accumulatedMudFee: '0',
    statistics: {
      orderCountOpen: 0,
      orderCountFilled: 0,
      orderCountCancelled: 0,
      openBuyUsdtTotal: '0',
      openSellMudTotal: '0',
      uniqueUserCount: 0,
      totalUsdtFeeCollected: '0',
      totalMudFeeCollected: '0'
    },
    loading: true
  })

  const fetchState = useCallback(() => {
    if (!orderBookContract) return
    setState(prev => ({ ...prev, loading: true }))
    Promise.all([
      orderBookContract.paused(),
      orderBookContract.maxPriceMultiplier(),
      orderBookContract.feeReceiver(),
      orderBookContract.accumulatedUsdtFee(),
      orderBookContract.accumulatedMudFee(),
      orderBookContract.getStatistics()
    ])
      .then(([paused, maxPriceMultiplier, feeReceiver, accumulatedUsdtFee, accumulatedMudFee, stats]) => {
        const s = stats as any
        const getStat = (key: string, idx: number) => s?.[key] ?? s?.[idx]
        setState({
          paused,
          maxPriceMultiplier: Number(maxPriceMultiplier.toString()),
          feeReceiver: feeReceiver.toString(),
          accumulatedUsdtFee: accumulatedUsdtFee.toString(),
          accumulatedMudFee: accumulatedMudFee.toString(),
          statistics: {
            orderCountOpen: Number(getStat('orderCountOpen', 0)?.toString() ?? 0),
            orderCountFilled: Number(getStat('orderCountFilled', 1)?.toString() ?? 0),
            orderCountCancelled: Number(getStat('orderCountCancelled', 2)?.toString() ?? 0),
            openBuyUsdtTotal: getStat('openBuyUsdtTotal', 3)?.toString() ?? '0',
            openSellMudTotal: getStat('openSellMudTotal', 4)?.toString() ?? '0',
            uniqueUserCount: Number(getStat('uniqueUserCount', 5)?.toString() ?? 0),
            totalUsdtFeeCollected: getStat('totalUsdtFeeCollected', 6)?.toString() ?? '0',
            totalMudFeeCollected: getStat('totalMudFeeCollected', 7)?.toString() ?? '0'
          },
          loading: false
        })
      })
      .catch(() => setState(prev => ({ ...prev, loading: false })))
  }, [orderBookContract])

  useEffect(() => {
    fetchState()
  }, [fetchState])

  return {
    ...state,
    refresh: fetchState
  }
}
